import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/providers/theme-context.tsx';
import { Session } from '@utils/types/editors/sessions.ts';
import { waveInfoErrorKey } from './wave-info-error.ts';

// Closer than a second is rounding or container framing, not a real edit.
const DURATION_MISMATCH_THRESHOLD_SECONDS = 1;

/** Audio-timeline state shown at the top of the media source dialog. */
export function WaveInfoNotices({ session }: { session?: Session }) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const status = session?.wave_info_status ?? null;
  // The theme has no warning token; emphasise with the body colour instead
  // of a hardcoded amber.
  const warning = { color: getColour('normalText'), fontWeight: 500 };

  const durationMismatch = useMemo(() => {
    if (status !== 'done') return null;
    const probed = session?.wave_info_probed_duration;
    const original = session?.duration;
    if (!probed || !original || original <= 0) return null;
    if (Math.abs(probed - original) < DURATION_MISMATCH_THRESHOLD_SECONDS) {
      return null;
    }
    return { probed, original };
  }, [status, session?.wave_info_probed_duration, session?.duration]);

  return (
    <>
      {status === 'stale' && (
        <p className="text-xs" style={warning}>
          {t('wave-info-stale-notice')}
        </p>
      )}
      {(status === 'pending' || status === 'running') && (
        <p className="text-xs" style={{ color: getColour('activeText') }}>
          {t('wave-info-regenerating-notice')}
        </p>
      )}
      {status === 'failed' && (
        <p
          className="text-xs"
          role="alert"
          style={{ color: getColour('danger') }}
        >
          {t(waveInfoErrorKey(session?.wave_info_error))}
        </p>
      )}
      {durationMismatch && (
        <p className="text-xs" style={warning}>
          {t('wave-info-duration-mismatch-notice', {
            probed: Math.round(durationMismatch.probed),
            original: Math.round(durationMismatch.original)
          })}
        </p>
      )}
    </>
  );
}
